import { useState, useEffect } from 'react';
import { BookOpen, ChevronDown, ChevronRight, FileText, Download, Eye } from 'lucide-react';
import api, { assetUrl } from '../../services/api';

export default function LecturerSubjects() {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGrades, setExpandedGrades] = useState([]);
  const [expandedSubjects, setExpandedSubjects] = useState([]);
  const [resources, setResources] = useState({});
  const [loadingResources, setLoadingResources] = useState({});

  useEffect(() => { loadSubjects(); }, []);

  const loadSubjects = async () => {
    try {
      const res = await api.get('/lecturer/subjects');
      if (res.data.success && res.data.data) {
        setGrades(res.data.data.grades || []);
        // Expand all grades by default
        setExpandedGrades(res.data.data.grades?.map(g => g.grade_id) || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const toggleGrade = (gradeId) => {
    setExpandedGrades(prev =>
      prev.includes(gradeId) ? prev.filter(id => id !== gradeId) : [...prev, gradeId]
    );
  };

  const toggleSubject = async (subjectId) => {
    if (expandedSubjects.includes(subjectId)) {
      setExpandedSubjects(expandedSubjects.filter(id => id !== subjectId));
    } else {
      setExpandedSubjects([...expandedSubjects, subjectId]);
      // Load resources if not already loaded
      if (!resources[subjectId]) {
        await loadResources(subjectId);
      }
    }
  };

  const loadResources = async (subjectId) => {
    setLoadingResources({ ...loadingResources, [subjectId]: true });
    try {
      const res = await api.get(`/lecturer/subjects/${subjectId}/resources`);
      if (res.data.success) {
        setResources({ ...resources, [subjectId]: res.data.data.resources || [] });
      }
    } catch (err) {
    } finally {
      setLoadingResources({ ...loadingResources, [subjectId]: false });
    }
  };

  const handleDownload = (filePath, fileName) => {
    // Direct file access (uploads directory is publicly accessible)
    const downloadUrl = assetUrl(filePath);
    // Create a temporary link to trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleView = (filePath) => {
    // Direct file access
    window.open(assetUrl(filePath), '_blank');
  }; 

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('word') || fileType?.includes('doc')) return '📝';
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('video')) return '🎥';
    return '📎';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">My Subjects</h1>
        <p className="text-gray-500">Subjects assigned to you grouped by grade</p>
      </div>

      <div className="space-y-4">
        {grades.map((grade) => (
          <div key={grade.grade_id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => toggleGrade(grade.grade_id)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition"
            >
              <div className="flex items-center gap-3">
                {expandedGrades.includes(grade.grade_id) ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
                <h3 className="text-lg font-semibold text-gray-800">{grade.grade_name}</h3>
                <span className="px-3 py-1 bg-indigo-100 text-indigo-600 rounded-full text-sm font-medium">
                  {grade.subjects.length} {grade.subjects.length === 1 ? 'Subject' : 'Subjects'}
                </span>
              </div>
            </button>

            {expandedGrades.includes(grade.grade_id) && (
              <div className="border-t border-gray-100">
                <div className="space-y-3 p-6">
                  {grade.subjects.map((subject) => (
                    <div key={subject.subject_id} className="bg-gray-50 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSubject(subject.subject_id)}
                        className="w-full p-4 hover:bg-gray-100 transition flex items-center justify-between"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-indigo-100 rounded-lg">
                            <BookOpen className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div className="text-left">
                            <h4 className="font-semibold text-gray-800">{subject.subject_name}</h4>
                            <p className="text-sm text-gray-500 mt-1">{subject.subject_code}</p>
                          </div>
                        </div>
                        {expandedSubjects.includes(subject.subject_id) ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                      </button>

                      {/* Resources Section */}
                      {expandedSubjects.includes(subject.subject_id) && (
                        <div className="px-4 pb-4 border-t border-gray-200">
                          <div className="mt-4">
                            <div className="flex items-center gap-2 mb-3">
                              <FileText className="w-5 h-5 text-indigo-600" />
                              <h5 className="font-semibold text-gray-800">Course Resources</h5>
                            </div>
                            
                            {loadingResources[subject.subject_id] ? (
                              <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                              </div>
                            ) : resources[subject.subject_id]?.length > 0 ? (
                              <div className="space-y-2">
                                {resources[subject.subject_id].map((resource) => (
                                  <div key={resource.resource_id} className="bg-white rounded-lg p-4 border border-gray-200 hover:border-indigo-300 transition">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-2xl">{getFileIcon(resource.file_type)}</span>
                                          <div>
                                            <h6 className="font-medium text-gray-800">{resource.resource_heading}</h6>
                                            <p className="text-sm text-gray-500">{resource.file_name}</p>
                                            <p className="text-xs text-gray-400 mt-1">
                                              Uploaded {new Date(resource.created_at).toLocaleDateString()} 
                                              {resource.uploaded_by_name && ` by ${resource.uploaded_by_name}`}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => handleView(resource.file_path)}
                                          className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                                          title="View"
                                        >
                                          <Eye className="w-5 h-5" />
                                        </button>
                                        <button
                                          onClick={() => handleDownload(resource.file_path, resource.file_name)}
                                          className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition"
                                          title="Download"
                                        >
                                          <Download className="w-5 h-5" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-gray-400 bg-white rounded-lg border border-gray-200">
                                <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                <p>No resources available yet</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        {grades.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <p className="text-gray-400">No subjects assigned to you yet. Contact admin.</p>
          </div>
        )}
      </div>
    </div>
  );
}
